const thirtySeconds = 30 * 1000
const fiveSeconds = 5 * 1000
const twoSeconds = 2 * 1000

export default class SSO {
  constructor (config, authStrategy) {
    if (config.features.ssoEnabled) {
      if (!localStorageSupported) {
        console.error("SSO disabled, local storage not supported")
        return new DisabledSSO()
      }
      return authStrategy === 'auth' ? (new AuthSSO(config)) : (new EnabledSSO(config))
    } else {
      return new DisabledSSO()
    }
  }
}

class DisabledSSO {
  getToken () {
    return Promise.resolve()
  }

  logout () {
    return Promise.resolve()
  }
}

class EnabledSSO {
  constructor (config) {
    this.origin = this.extractOriginUrl(config)
    this.ssoIframeUrl = this.getSsoIframeUrl()
    this.connectingPromise = this.connect()
    this.TATokenKey = 'TAToken'
    this.postMessageTypes = {
      getMessage: 'get',
      deleteMessage: 'unset',
    }
    this.unexpectedMessageError = (data) => `SSO message handler got an unexpected message: ${JSON.stringify(data)}`
  }

  extractOriginUrl (config) {
    return config.ssoUrl && config.ssoUrl.replace(/\/$/,'')
  }

  getSsoIframeUrl () {
    return `${this.origin}/AccountManagement/CrossDomainStorageFrame.html`
  }

  connect () {
    if (!this.connectingPromise) {
      this.connectingPromise = promiseTimeout(this.loadIframe(), thirtySeconds)
    }
    return this.connectingPromise
  }

  loadIframe () {
    return new Promise((resolve, reject) => {
      try {
        this.iframe = document.createElement("iframe")
        this.iframe.style.cssText = "position:absolute;width:1px;height:1px;left:-9999px;"
        document.body.appendChild(this.iframe)

        if (window.addEventListener) {
          this.iframe.addEventListener("load", resolve, false)
          this.iframe.addEventListener("error", reject, false)
          window.addEventListener("message", (event) => this.handleMessage(event), false)
        } else if (this.iframe.attachEvent) {
          this.iframe.attachEvent("onload", resolve, false)
          this.iframe.attachEvent("onerror", reject, false)
          window.attachEvent("onmessage", (event) => this.handleMessage(event))
        }
        this.iframe.src = this.ssoIframeUrl
      } catch (err) {
        reject(err)
      }
    })
  }

  getMessageId (type) {
    return [type, this.TATokenKey].join(':')
  }

  handleMessage (event) {
    if (this.origin && event.origin === this.origin) {
      if (typeof event.data !== "string") {
        return
      }

      try {
        const data = JSON.parse(event.data)

        const isMessageBelongsToUs = Object
          .values(this.postMessageTypes)
          .map(type => this.getMessageId(type))
          .some(item => item === data.id)

        if (data.error) {
          this.promiseResolvers.reject(data.error)
        } else if (isMessageBelongsToUs) {
          this.promiseResolvers.resolve(data?.value)
        } else {
          console.error(this.unexpectedMessageError(data))
        }
      } catch (error) {
        this.promiseResolvers.reject(error)
      }
    }
  }

  getToken () {
    return this.connect()
      .then(() => {
        return promiseTimeout(new Promise((resolve, reject) => {
          this.promiseResolvers = {resolve, reject}

          const {getMessage} = this.postMessageTypes

          const body = JSON.stringify({
            id: this.getMessageId(getMessage),
            type: getMessage,
            key: this.TATokenKey,
          })

          try {
            this.iframe.contentWindow.postMessage(body, this.origin)
          } catch (err) {
            reject(err)
          }
        }), fiveSeconds)
      })
      .catch(err => {
        console.error(err)
      })
  }

  logout () {
    return this.connect()
      .then(() => {
        return promiseTimeout(new Promise((resolve, reject) => {
          this.promiseResolvers = {resolve, reject}

          const {deleteMessage} = this.postMessageTypes

          const body = JSON.stringify({
            id: this.getMessageId(deleteMessage),
            type: deleteMessage,
            key: this.TATokenKey,
          })

          try {
            localStorage.removeItem(this.TATokenKey)

            this.iframe.contentWindow.postMessage(body, this.origin)
          } catch (err) {
            reject(err)
          }
        }), twoSeconds)
      })
      .catch(err => {
        console.error(err)
      })
  }
}

class AuthSSO extends EnabledSSO {
  constructor (config) {
    super(config)

    // due to differences in iframe javascripts handling
    // `set`, `get`, `unset` localstorage for SSO and
    // `set`, `get`, `delete` localstorage for auth via sending postMessages
    this.postMessageTypes = {
      ...this.postMessageTypes,
      deleteMessage: 'delete',
    }
    this.unexpectedMessageError = (data) => `AUTH message handler got an unexpected message: ${JSON.stringify(data)}`
  }

  extractOriginUrl (config) {
    return config.authSSOWebUrl && config.authSSOWebUrl.replace(/\/$/,'')
  }

  getSsoIframeUrl () {
    return `${this.origin}/CrossDomain.html`
  }
}

const promiseTimeout = function (promise, timeoutMS) {
  const timeout = new Promise((resolve, reject) => {
    const id = setTimeout(() => {
      clearTimeout(id)
      reject(new Error('SSO timed out in ' + timeoutMS + 'ms.'))
    }, timeoutMS)
  })

  return Promise.race([
    promise,
    timeout,
  ])
}

const localStorageSupported = (function () {
  try {
    return window.postMessage && window.JSON && 'localStorage' in window && window['localStorage'] !== null
  } catch (e) {
    return false
  }
})()